import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './dialog';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Textarea } from './textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import AdvancedColorPicker from './AdvancedColorPicker';
import { Plus, Trash2, Palette, Wand2, RotateCcw, Edit3, Eye, XIcon, Check } from 'lucide-react';
import type { ColorPalette, CreateColorPalette } from '@shared/schemas/colorPalette';
import { colorPaletteService } from '../../services/colorPaletteService';
import { generatePalette, type PaletteMode } from '../../lib/paletteGenerator';
import { auth } from '../../config/firebase';
import { getSimpleColorName } from '@/lib/colorUtils';

interface CustomPaletteCreatorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (palette: ColorPalette) => Promise<void>; // Updated to pass full palette
  editingPalette?: ColorPalette | null;
  userId: string;
  organizationId: string;
}

const DEFAULT_COLORS = ['#ef4444', '#f97316', '#f59e0b', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#6b7280'];

// Palette generation mode options
const GENERATION_MODES: { value: PaletteMode; label: string; description: string }[] = [
  {
    value: 'gradient',
    label: 'Gradient',
    description: 'Creates lighter and darker variations of your base color'
  },
  {
    value: 'monochromatic',
    label: 'Monochromatic',
    description: 'Uses the same hue with different saturation and lightness levels'
  },
  {
    value: 'analogous',
    label: 'Analogous',
    description: 'Uses colors adjacent to your base color on the color wheel'
  },
  {
    value: 'complementary',
    label: 'Complementary',
    description: 'Combines your base color with its complement and variations'
  }
];

const CustomPaletteCreator: React.FC<CustomPaletteCreatorProps> = ({
  isOpen,
  onClose,
  onSave,
  editingPalette,
  userId,
  organizationId,
}) => {
  // State for Name and Description (will be used at the end)
  const [paletteName, setPaletteName] = useState('');
  const [paletteDescription, setPaletteDescription] = useState('');
  
  const [paletteColors, setPaletteColors] = useState<string[]>(DEFAULT_COLORS);
  const [selectedDefaultColor, setSelectedDefaultColor] = useState<string | undefined>(undefined); // New state for explicit default color
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  
  const [baseColor, setBaseColor] = useState('#3B82F6'); // Changed to uppercase hex
  const [generationMode, setGenerationMode] = useState<PaletteMode>('gradient');
  const [generatedPalette, setGeneratedPalette] = useState<string[]>([]);
  
  const [activeManualColor, setActiveManualColor] = useState<{ index: number; color: string } | null>(null); // This will be for editing individual colors later if needed
  const colorPickerRef = useRef<HTMLDivElement>(null); // For popover positioning
  const previousColorsLengthRef = useRef(paletteColors.length);

  // Effect to reset state when dialog opens and when editingPalette changes
  useEffect(() => {
    if (isOpen) {
      setError('');
      if (editingPalette) {
        setPaletteName(editingPalette.name || '');
        setPaletteDescription(editingPalette.description || '');
        setPaletteColors(editingPalette.colors || DEFAULT_COLORS);
        setBaseColor(editingPalette.colors?.[0] || '#3B82F6'); // Default base color to first palette color or standard default
        setSelectedDefaultColor(editingPalette.defaultColor); // Load existing default color
      } else {
        setPaletteName('');
        setPaletteDescription('');
        setPaletteColors(DEFAULT_COLORS);
        setBaseColor('#3B82F6');
        setSelectedDefaultColor(undefined); // Clear for new palette
      }
      setGenerationMode('gradient');
      setGeneratedPalette([]);
      setActiveManualColor(null);
    }
  }, [isOpen, editingPalette]);

  // Effect to close popover if the number of colors changes (e.g., add/remove/replace all)
  useEffect(() => {
    if (paletteColors.length !== previousColorsLengthRef.current) {
      setActiveManualColor(null);
      previousColorsLengthRef.current = paletteColors.length;
    }
  }, [paletteColors]);

  // Click outside listener for color picker popover
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
        setActiveManualColor(null);
      }
    }
    if (activeManualColor) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeManualColor]);

  const handleAddColor = () => {
    if (paletteColors.length < 12) {
      const newColor = '#CCCCCC';
      setPaletteColors(prev => [...prev, newColor]);
      // setActiveManualColor({ index: paletteColors.length, color: newColor }); // Keep new color editor closed by default for now
      setActiveManualColor(null); // Ensure any open editor is closed
    }
  };

  const handleRemoveColor = (indexToRemove: number) => {
    if (paletteColors.length > 1) {
      setPaletteColors(prev => prev.filter((_, index) => index !== indexToRemove));
      setActiveManualColor(null); // Always close popover on remove
    }
  };

  const handleManualColorChange = (newColor: string) => {
    if (activeManualColor !== null) {
      const updatedColors = [...paletteColors];
      updatedColors[activeManualColor.index] = newColor;
      setPaletteColors(updatedColors);
      // Update the color within the activeManualColor state as well to keep the picker synced
      setActiveManualColor(prev => prev ? { ...prev, color: newColor } : null);
    }
  };

  const handleSwatchClick = (index: number, color: string) => {
    // Primary action: Set as default color
    setSelectedDefaultColor(color);

    // Secondary action (for later): open color editor for this swatch
    // For now, we disable opening the editor on simple click to avoid conflict
    // if (activeManualColor && activeManualColor.index === index) {
    //   setActiveManualColor(null); // Toggle off if already active
    // } else {
    //   setActiveManualColor({ index, color });
    // }
  };
  
  const handleGeneratePalette = () => {
    const result = generatePalette(baseColor, generationMode);
    const newColors = Array.isArray(result) ? result : result.colors; // Ensure we always pass string[]
    setGeneratedPalette(newColors);
  };

  const handleUseGeneratedPalette = () => {
    if (generatedPalette.length > 0) {
      setPaletteColors(generatedPalette);
      setSelectedDefaultColor(baseColor); // Set baseColor as default when using generated palette
      
      // Auto-generate a name if none exists and user hasn't typed one
      if (!paletteName.trim() && editingPalette?.name !== paletteName) {
        const commonName = getSimpleColorName(baseColor);
        const modeName = GENERATION_MODES.find(m => m.value === generationMode)?.label || 'Generated';
        const hexPart = baseColor.replace('#', '').toUpperCase();
        
        if (commonName) {
          setPaletteName(`${commonName.charAt(0).toUpperCase() + commonName.slice(1)} - ${modeName} Palette (#${hexPart})`);
        } else {
          setPaletteName(`${modeName} Palette (#${hexPart})`);
        }
      }
      
      setGeneratedPalette([]);
      setActiveManualColor(null);
      if (error) setError('');
    }
  };

  // Consider also updating the name if baseColor changes AND the name was auto-generated previously
  // This might be too aggressive, but is an option:
  useEffect(() => {
    // If the palette name seems auto-generated and baseColor changes, regenerate the name part
    // This is a heuristic and might need refinement
    const hexInNameMatch = paletteName.match(/\(#([0-9A-Fa-f]{6})\)$/);
    const currentHexInName = hexInNameMatch ? `#${hexInNameMatch[1]}` : null;

    if (paletteName && currentHexInName && currentHexInName.toUpperCase() !== baseColor.toUpperCase() && !editingPalette) {
        // Only update if name looks auto-generated from a *different* baseColor and it's a new palette
        const commonName = getSimpleColorName(baseColor);
        const modeName = GENERATION_MODES.find(m => m.value === generationMode)?.label || 'Generated';
        const newHexPart = baseColor.replace('#', '').toUpperCase();
        if (commonName) {
            setPaletteName(`${commonName.charAt(0).toUpperCase() + commonName.slice(1)} - ${modeName} Palette (#${newHexPart})`);
        } else {
            setPaletteName(`${modeName} Palette (#${newHexPart})`);
        }
    }
  }, [baseColor, generationMode, paletteName, editingPalette]); // Add other relevant dependencies

  const handleSave = async () => {
    if (!paletteName.trim() || paletteColors.length === 0) {
        setError('Palette name and at least one color are required.');
        return;
    }
    setSaving(true);
    setError('');
    
    const trimmedDescription = paletteDescription.trim();

    // Base data for both create and update
    const commonPaletteData: any = {
        name: paletteName.trim(),
        colors: paletteColors,
        defaultColor: selectedDefaultColor, // Add selectedDefaultColor to save data
    };

    // Only add description if it's not empty
    if (trimmedDescription) {
        commonPaletteData.description = trimmedDescription;
    }

    try {
        if (editingPalette) {
            // For update, we might want to allow explicitly setting description to empty or null
            // If your update logic in colorPaletteService handles { description: null } to remove it, that's fine.
            // Or, ensure the update payload only includes changed fields.
            // For now, let's assume update should also only send description if it has content or is being explicitly cleared.
            const updateData = { ...commonPaletteData };
            if (!trimmedDescription && editingPalette.description) {
                // If description was cleared, send null or handle field removal in service
                // For simplicity here, we'll just not send it if empty, relying on merge behavior or service logic
            } else if (trimmedDescription) {
                updateData.description = trimmedDescription;
            } // If trimmedDescription is empty and editingPalette.description was also empty, no change to description

            await colorPaletteService.update(editingPalette.id, updateData, userId);
            const updatedPalette = await colorPaletteService.getById(editingPalette.id);
            if (updatedPalette) onSave(updatedPalette);
        } else {
            // For create, commonPaletteData already correctly omits description if empty
            const newPaletteData: CreateColorPalette = {
                ...commonPaletteData, // This will not have description if trimmedDescription was empty
                ownerId: userId,
                organizationId: organizationId,
                visibility: 'organization', 
                tags: [],
                // usageCount, createdAt, updatedAt are set by the service
            };
            const newPalette = await colorPaletteService.create(newPaletteData);
            onSave(newPalette);
        }
        onClose();
    } catch (err) {
      console.error('Error saving custom palette:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save palette.';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };
  
  const handleCancel = () => {
    onClose();
  };

  const isValidToSave = paletteName.trim().length > 0 && paletteColors.length > 0;

  // Popover positioning - simplified to be more generic for now
  // This will appear below the "Manually Selected Colors" title area.
  const popoverStyle: React.CSSProperties = activeManualColor 
    ? { 
        position: 'relative', // Or 'absolute' if you have a positioned parent for manual section
        zIndex: 20, 
        // Remove top/left calculations for now, let it flow or be styled by its container
      }
    : {};

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleCancel(); }}>
      <DialogContent className="w-[95vw] lg:w-[80vw] max-h-[95vh] flex flex-col p-0 !max-w-none sm:!max-w-none">
        <DialogHeader className="p-4 sm:p-6 border-b flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5 text-primary" />
            {editingPalette ? 'Edit Custom Palette' : 'Create Custom Palette'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-8">
          {error && (
            <div className="mb-4 rounded-md bg-destructive/10 border border-destructive/20 p-3">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* SECTION 1: CHOOSE YOUR COLORS */}
          <div className="space-y-5">
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-primary border border-primary text-sm font-bold shrink-0">1</span>
              <h2 className="text-xl font-semibold">Choose Your Colors</h2>
            </div>

            {/* Auto-Generation Subsection - Corrected Grouping for Responsiveness */}
            <div className="ml-3 mr-3 sm:ml-8 sm:mr-0">
              <div className="space-y-4 p-4 border rounded-lg bg-muted/30"> {/* Reduced space-y to 4, individual items will have space-y if needed */}
                <div className="flex items-center gap-2 mb-2">
                  <Wand2 className="h-4 w-4 text-primary" />
                  <h3 className="text-md font-semibold">Method A: Auto-Generate (Optional)</h3>
                </div>
                
                {/* Combined Grid for Steps 1 & 2 - each child is a Title+Control Group */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 items-stretch md:items-end"> {/* items-stretch for full height, md:items-end for bottom align on larger */}
                  {/* Group 1: Base Color */}
                  <div className="space-y-1.5 flex flex-col justify-between"> {/* Flex col to allow picker to push select down if needed for alignment */}
                    <Label className="text-sm font-medium block">1. Select Base Color</Label>
                    <AdvancedColorPicker
                      color={baseColor}
                      onChange={setBaseColor}
                    />
                  </div>
                  
                  {/* Group 2: Generation Mode */}
                  <div className="space-y-1.5 flex flex-col justify-between"> {/* Flex col to allow select to align with potentially taller picker */}
                    <Label className="text-sm font-medium block md:mb-7">2. Choose Generation Mode</Label>
                    <Select value={generationMode} onValueChange={(value: PaletteMode) => setGenerationMode(value)}>
                      <SelectTrigger id="generation-mode" className="h-16 text-sm w-full mt-auto"> {/* mt-auto to push to bottom within flex col if needed */}
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-[400px]">
                        {GENERATION_MODES.map((mode) => (
                          <SelectItem key={mode.value} value={mode.value} className="text-sm ">
                            <div className="flex flex-col"><span>{mode.label}</span><span className="text-xs text-muted-foreground">{mode.description}</span></div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Step 3: Generate Palette Button */}
                <div className="mt-4 flex justify-end">
                  <Button variant="outline" size="default" onClick={handleGeneratePalette} className="h-12 text-sm whitespace-nowrap flex items-center gap-2 group hover:bg-primary/10 hover:border-primary hover:text-primary transition-colors duration-150 px-6">
                    <Wand2 className="h-4 w-4 transition-transform duration-300 ease-out group-hover:scale-125" />
                    3. Generate Palette
                  </Button>
                </div>
                {generatedPalette.length > 0 && (
                  <div className="mt-5 space-y-2">
                    <Label className="text-xs">Review & Use Generated Colors</Label>
                    <div className="p-3 border rounded-md bg-background">
                      <div className="flex flex-wrap gap-1.5 mb-1.5">
                        {generatedPalette.map((color, index) => (<div key={`gen-${index}`} className="h-7 w-7 border border-border/20 rounded-sm shadow-sm" style={{ backgroundColor: color }} title={color} />))}
                      </div>
                      <div className="text-xs text-muted-foreground">{GENERATION_MODES.find(m => m.value === generationMode)?.label} • {generatedPalette.length} colors</div>
                    </div>
                    <Button variant="default" size="sm" onClick={handleUseGeneratedPalette} className="w-full text-xs h-9">
                      <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                      Apply These Colors to Manual Selection Below
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* "OR" Separator */}
            <div className="flex items-center gap-x-3 sm:gap-x-4 px-3 sm:px-8 py-3">
              <div className="flex-grow border-t border-border"></div>
              <span className="text-xs font-semibold text-muted-foreground tracking-wider">OR</span>
              <div className="flex-grow border-t border-border"></div>
            </div>

            {/* Manually Selected Colors Subsection - Revised for dynamic swatch width */}
            <div className="space-y-2.5 ml-3 mr-3 sm:ml-8 sm:mr-0">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-2.5">
                <div>
                  <h3 className="text-md font-semibold">Method B: Manually Select Colors ({paletteColors.length})</h3>
                  <p className="text-xs text-muted-foreground">Click a swatch to edit. Minimum 1, max 12 colors.</p>
                </div>
                <Button variant="outline" size="sm" onClick={handleAddColor} disabled={paletteColors.length >= 12} className="text-xs h-9 whitespace-nowrap flex-shrink-0">
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Color
                </Button>
              </div>
              
              {paletteColors.length > 0 ? (
                // Using flexbox with wrapping for swatches. Swatches will try to fill width and shrink.
                <div className="flex flex-wrap gap-1.5 p-1 bg-muted/20 rounded-md min-h-[2.75rem]"> {/* gap-1.5 for tighter packing */}
                  {paletteColors.map((color, index) => (
                    <div 
                      key={index} 
                      className="relative rounded border border-border/30 cursor-pointer hover:ring-2 hover:ring-primary/70 focus-within:ring-2 focus-within:ring-primary/70 transition-all shadow-sm group"
                      // Dynamic sizing: flex-grow, flex-shrink, flex-basis, aspect-ratio
                      style={{ 
                        backgroundColor: color, 
                        flexGrow: 1, 
                        flexShrink: 1, 
                        flexBasis: '2rem', // Start small, let it grow. Adjust as needed.
                        minWidth: '1.75rem', // min-w-7, smallest reasonable touch target
                        maxWidth: '75px', // Max width 75px
                        maxHeight: '75px', // Max height 75px (aspectRatio will enforce square)
                        aspectRatio: '1 / 1', // Keep it square
                      }} 
                      onClick={() => handleSwatchClick(index, color)} title={`Set ${color.toUpperCase()} as default. Click again to edit.`} tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSwatchClick(index, color); }}
                    >
                      {color.toUpperCase() === selectedDefaultColor?.toUpperCase() && (
                        <div className="absolute inset-0 flex items-center justify-center rounded">
                          <Check className="h-1/2 w-1/2 text-white" />
                        </div>
                      )}
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleRemoveColor(index); }}
                        // Adjusted X button size/position for potentially smaller swatches
                        className="absolute -top-1 -right-1 z-10 p-0.5 bg-white text-gray-500 border border-gray-200 rounded-full hover:bg-gray-100 hover:text-red-500 hover:border-red-300 transition-all opacity-0 group-hover:opacity-100 focus:opacity-100"
                        aria-label="Remove color"
                        title="Remove color"
                      >
                        <XIcon className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (<p className="text-sm text-muted-foreground text-center py-4">No colors selected. Add or use auto-generator.</p>)}
              
              {/* Info about default color selection */}
              {paletteColors.length > 0 && (
                <div className="mt-2 text-xs text-muted-foreground flex items-center gap-2">
                  <span>Click a color to change default:</span>
                  {selectedDefaultColor ? (
                    <>
                      <div className="w-6 h-6 rounded-sm border" style={{ backgroundColor: selectedDefaultColor }}></div>
                      <span className="font-mono">{selectedDefaultColor.toUpperCase()}</span>
                    </>
                  ) : (
                    <span>None (Click a swatch above to set)</span>
                  )}
                </div>
              )}
            </div>
          </div> 
          
          {/* SECTION 2: NAME YOUR PALETTE */}
          <div className="space-y-4 pt-6 border-t">
            <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-primary border border-primary text-sm font-bold shrink-0">2</span>
                <h2 className="text-xl font-semibold">Name Your Palette</h2>
            </div>
            {/* Palette Name and Description side-by-side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 ml-3 mr-3 sm:ml-8 sm:mr-0">
              <div className="space-y-1.5">
                <Label htmlFor="palette-name-input" className="text-sm font-medium">Palette Name *</Label>
                <Input
                  id="palette-name-input"
                  value={paletteName} // Ensure this uses paletteName state
                  onChange={(e) => setPaletteName(e.target.value)}
                  placeholder="e.g., Vibrant Blues & Greens"
                  maxLength={50}
                  className="text-sm h-11"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="palette-description-input" className="text-sm font-medium">Description (Optional)</Label>
                <Textarea
                  id="palette-description-input"
                  value={paletteDescription} // Ensure this uses paletteDescription state
                  onChange={(e) => setPaletteDescription(e.target.value)}
                  placeholder="A brief description of this palette..."
                  rows={3}
                  maxLength={200}
                  className="text-sm min-h-[2.75rem]" // h-11
                />
              </div>
            </div>
          </div>

          {/* Final Preview REMOVED */}

        </div> {/* End of scrollable content area */}

        <DialogFooter className="p-4 sm:p-6 border-t bg-background flex-shrink-0">
             <div className="flex w-full gap-3 justify-end">
                <Button variant="outline" onClick={handleCancel} disabled={saving} className="min-w-[100px]">
                    Cancel
                </Button>
                <Button onClick={handleSave} disabled={!isValidToSave || saving} className="min-w-[120px]">
                    {saving ? 'Saving...' : editingPalette ? 'Update Palette' : 'Create Palette'}
                </Button>
            </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CustomPaletteCreator; 